/**
 * WALKTHROUGH 09-29 #13 (approved v3): the transcript read, on one column with
 * a labelled progress bar, in the upload panel and in the Skills Survey.
 *
 * ROLLBACK: set this to false. The panel goes back to closing itself 600ms
 * into a read, its old reading view comes back, and the Skills Survey shows
 * the rotating wait (cpd-transcript-wait) again. Nothing else reads it.
 */
export const TRANSCRIPT_READ_V3 = true;

/**
 * What a read actually measures, not what we wish it took. The bar is drawn
 * against this, and the copy above it says "about a minute" for the same
 * reason: 03 section 5 records the measurement as 30 to 45 seconds and the
 * observed figure as around 50.
 */
export const MEDIAN_READ_MS = 50_000;

/**
 * Where the estimate stops until the reader returns. Arriving at 100% while
 * the reader is still working would be a claim we cannot make.
 */
export const READ_PROGRESS_CAP = 92;

/**
 * How far through the expected read we are, as a percentage: elapsed time
 * against the median, capped below full. The reader reports no stages, so
 * this is an estimate of time, not a measure of the document.
 */
export function estimateReadProgress(elapsedMs: number): number {
  if (elapsedMs <= 0) {
    return 0;
  }
  return Math.min(
    READ_PROGRESS_CAP,
    Math.round((elapsedMs / MEDIAN_READ_MS) * READ_PROGRESS_CAP)
  );
}

export type ReadKind = 'occ' | 'crs' | 'tst';

export interface ReadStep {
  key: ReadKind;
  /** The row in "What we are pulling out". */
  label: string;
  /** What the bar says while this third of the estimate runs. */
  step: string;
}

export const READ_STEPS: ReadonlyArray<ReadStep> = [
  {
    key: 'occ',
    label: 'Military occupations',
    step: 'Reading your military occupations'
  },
  {
    key: 'crs',
    label: 'Courses and ACE credit',
    step: 'Reading your courses and ACE credit'
  },
  {
    key: 'tst',
    label: 'College-level tests',
    step: 'Reading your college-level tests'
  }
];

/**
 * Which of the three the estimate is in. They split the capped range evenly,
 * because the reader does not say which one it is on.
 */
export function readStepIndex(progress: number): number {
  return Math.min(
    READ_STEPS.length - 1,
    Math.max(0, Math.floor(progress / (READ_PROGRESS_CAP / READ_STEPS.length)))
  );
}

/** The words beside the percentage. */
export function readStepLabel(progress: number): string {
  return progress >= READ_PROGRESS_CAP
    ? 'Finishing up'
    : READ_STEPS[readStepIndex(progress)].step;
}

/** The words at the other end of the bar. */
export function readTimeLeft(progress: number): string {
  const seconds = Math.round(
    (MEDIAN_READ_MS / 1000) * (1 - progress / READ_PROGRESS_CAP)
  );
  return seconds <= 3 ? 'Almost done' : `About ${seconds} seconds left`;
}
